import { newIdempotencyKey } from '@micarahr/api-client';
import type {
  PayrollExceptionKind,
  PayrollLine,
  PayrollRun,
  PayrollRunStatus,
} from '@micarahr/shared-types';
import { moneyFromDecimalString, moneyFromDecimalStringOrZero } from '@micarahr/shared-utils';
import type { Page } from './collections';
import { apiTransport } from './api-client';

/**
 * Payroll operations, against the engine.
 *
 * Three rules the server owns and this adapter must not paraphrase.
 *
 * **The transition table is the server's.** The prototype checked
 * `canTransition` in the browser before calling; that check is now advisory at
 * best and misleading at worst, because a period's state can change between the
 * screen loading and the button being pressed. The adapter calls the action and
 * shows what came back — a refusal that names the current state is more useful
 * than a disabled button that gives no reason.
 *
 * **Money is not a float.** Amounts arrive as decimal strings and are converted
 * to integer centavos on the digits, never through `Number(x) * 100`. Nothing
 * here adds or subtracts: the engine already produced these figures against a
 * versioned configuration, and a total recomputed in the browser is a second
 * answer to a question that already has one.
 *
 * **Calculation is a job, not a request.** A run over four thousand employees
 * does not fit in an HTTP response, so calculate starts it and the screen polls
 * the period for progress.
 */

export interface NewPayrollPeriod {
  payrollGroupId: string;
  label: string;
  periodStart: string;
  periodEnd: string;
  payDate: string;
}

interface ApiPayrollPeriod {
  id: string;
  referenceCode: string;
  label: string;
  payrollGroupId: string;
  payrollGroup: { id: string; name: string } | null;
  periodStart: string;
  periodEnd: string;
  payDate: string;
  status: string;
  attendanceImportedAt: string | null;
  calculatedAt: string | null;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedByUserId: string | null;
  submittedByUserId?: string | null;
  lockedAt: string | null;
  publishedAt: string | null;
  completedAt?: string | null;
  reopenedAt?: string | null;
  reopenReason: string | null;
  stepActors?: Record<string, string>;
  earlierCycles?: PayrollRun['earlierCycles'];
  createdAt: string;
  revision: number;
  _count?: { results?: number };
  totals?: {
    employeeCount: number;
    excludedCount: number | null;
    grossPay: string | null;
    totalDeductions: string | null;
    totalContributions: string | null;
    totalTax: string | null;
    netPay: string | null;
    exceptionCount: number;
    warningCount?: number;
  } | null;
}

function toPayrollRun(row: ApiPayrollPeriod): PayrollRun {
  return {
    id: row.id,
    periodId: row.id,
    label: row.label,
    periodStart: row.periodStart.slice(0, 10),
    periodEnd: row.periodEnd.slice(0, 10),
    payDate: row.payDate.slice(0, 10),
    payrollGroupId: row.payrollGroupId,
    payrollGroupName: row.payrollGroup?.name ?? '',
    status: row.status as PayrollRunStatus,
    /*
     * Null, not zero, when the period has not been calculated.
     *
     * A draft has no totals to send, and the screen prints an em dash for
     * null. It used to print ₱NaN — the strings were passed to a formatter
     * that reads `.minorUnits`, and a cast to `PayrollRun` kept the compiler
     * from saying so.
     */
    totals: {
      employeeCount: row.totals?.employeeCount ?? row._count?.results ?? 0,
      excludedCount: row.totals?.excludedCount ?? 0,
      grossPay: moneyFromDecimalString(row.totals?.grossPay),
      totalDeductions: moneyFromDecimalString(row.totals?.totalDeductions),
      totalContributions: moneyFromDecimalString(row.totals?.totalContributions),
      totalTax: moneyFromDecimalString(row.totals?.totalTax),
      netPay: moneyFromDecimalString(row.totals?.netPay),
      exceptionCount: row.totals?.exceptionCount ?? 0,
      warningCount: row.totals?.warningCount ?? 0,
    },
    attendanceImportedAt: row.attendanceImportedAt,
    calculatedAt: row.calculatedAt,
    submittedAt: row.submittedAt,
    approvedAt: row.approvedAt,
    approvedByUserId: row.approvedByUserId,
    submittedByUserId: row.submittedByUserId ?? null,
    lockedAt: row.lockedAt,
    publishedAt: row.publishedAt,
    completedAt: row.completedAt ?? null,
    reopenedAt: row.reopenedAt ?? null,
    reopenReason: row.reopenReason,
    stepActors: (row.stepActors ?? {}) as PayrollRun['stepActors'],
    earlierCycles: row.earlierCycles ?? [],
    createdAt: row.createdAt,
  };
}

interface ApiPayrollLineItem {
  code: string;
  label: string;
  category: string;
  amount: string;
  basis?: string;
  employerAmount?: string;
}

interface ApiPayrollEmployee {
  id: string;
  employeeId: string;
  employee: { id: string; fullName: string; employeeNumber: string } | null;
  grossPay: string;
  totalEarnings: string;
  totalDeductions: string;
  totalContributions: string;
  totalTax: string;
  netPay: string;
  lineItems: ApiPayrollLineItem[];
  inputSnapshot: Record<string, unknown>;
}

interface ApiPayrollException {
  id: string;
  employeeId: string | null;
  code: string;
  message: string;
  severity: string;
  suggestedAction?: string | null;
  resolvedAt?: string | null;
  resolvedByName?: string | null;
  resolutionNote?: string | null;
  waivable?: boolean;
  employee?: { id: string; fullName: string; employeeNumber: string } | null;
}

/** One finding from a calculation, as the period panel lists it. */
export interface PayrollPeriodException {
  id: string;
  employeeId: string | null;
  employeeName: string | null;
  employeeNumber: string | null;
  code: string;
  severity: 'info' | 'warning' | 'blocking';
  message: string;
  suggestedAction: string | null;
  resolvedAt: string | null;
  resolvedByName: string | null;
  resolutionNote: string | null;
  /** False where a reason cannot clear it and the input must change instead. */
  waivable: boolean;
}

/** An employee left out of a period's run, and why. */
export interface PayrollPeriodExclusion {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeNumber: string;
  reason: string;
  excludedByName: string | null;
  createdAt: string;
}

/**
 * One employee's calculated pay.
 *
 * A line only exists on a calculated run, so an absent amount here means the
 * component does not apply — no overtime, no loan — and zero is the right
 * reading. That is why this uses the zero-defaulting conversion where the run
 * totals use the nullable one.
 *
 * Nothing sums. Where a browser total and the payslip disagree, the payslip is
 * right.
 */
function toPayrollLine(
  row: ApiPayrollEmployee,
  periodId: string,
  exceptions: ApiPayrollException[],
): PayrollLine {
  const lines = row.lineItems ?? [];
  const find = (code: string) =>
    moneyFromDecimalStringOrZero(lines.find((item) => item.code === code)?.amount);
  const snapshot = (row.inputSnapshot ?? {}) as Record<string, number>;

  return {
    id: row.id,
    runId: periodId,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    employeeNumber: row.employee?.employeeNumber ?? '',
    departmentName: '',
    basicPay: find('BASIC'),
    scheduledDays: snapshot.scheduledDays ?? 0,
    actualDays: snapshot.actualDays ?? 0,
    lateMinutes: snapshot.lateMinutes ?? 0,
    undertimeMinutes: snapshot.undertimeMinutes ?? 0,
    overtimeHours: snapshot.overtimeHours ?? 0,
    overtimePay: find('OVERTIME'),
    allowances: find('ALLOWANCE'),
    otherEarnings: find('OTHER_EARNINGS'),
    grossPay: moneyFromDecimalStringOrZero(row.grossPay),
    absenceDeduction: find('ABSENCE'),
    lateDeduction: find('LATE'),
    loanDeduction: find('LOAN'),
    otherDeductions: find('OTHER_DEDUCTIONS'),
    // Contributions keep their employer share: a payslip that shows only the
    // employee's half understates what the employment costs and what was
    // remitted on their behalf.
    contributions: lines
      .filter((item) => item.category === 'contribution')
      .map((item) => ({
        code: item.code,
        label: item.label,
        employee: moneyFromDecimalStringOrZero(item.amount),
        employer: moneyFromDecimalStringOrZero(item.employerAmount),
      })),
    taxWithheld: moneyFromDecimalStringOrZero(row.totalTax),
    adjustments: [],
    netPay: moneyFromDecimalStringOrZero(row.netPay),
    exceptions: exceptions.map((exception) => ({
      kind: exception.code as PayrollExceptionKind,
      message: exception.message,
    })),
    excluded: false,
    excludedReason: null,
  };
}

/**
 * The action each target status is reached by.
 *
 * A map rather than a switch so an unmapped status is a visible gap rather than
 * a silent no-op that leaves the period exactly where it was.
 *
 * `calculating` and `calculated` share one action. The console shows the run as
 * two steps (Calculate, then Finish calculation), but the server calculates in
 * a single call that passes through `calculating` and lands on `calculated` —
 * so "Calculate" is that call, and a period left in `calculating` (an
 * interrupted run) is finished by calling it again. Unmapped, the first
 * refused with "No payroll action reaches calculating" and no period could be
 * calculated from the console at all.
 *
 * `payslips_published` is the separate publish route: publishing creates the
 * payslips, it is not a status change alone.
 */
const TRANSITION_ACTIONS: Partial<Record<PayrollRunStatus, string>> = {
  attendance_review: 'import-attendance',
  input_review: 'review-inputs',
  calculating: 'calculate',
  calculated: 'calculate',
  payslips_published: 'publish-payslips',
  for_approval: 'submit',
  approved: 'approve',
  locked: 'lock',
  completed: 'complete',
  reopened: 'reopen',
};

export const apiPayrollService = {
  async listRuns(): Promise<PayrollRun[]> {
    const rows = await apiTransport().get<ApiPayrollPeriod[]>('/api/v1/payroll-periods');
    return rows.map(toPayrollRun);
  },

  async getRun(id: string): Promise<PayrollRun | null> {
    try {
      return toPayrollRun(
        await apiTransport().get<ApiPayrollPeriod>(`/api/v1/payroll-periods/${id}`),
      );
    } catch (error) {
      if ((error as { status?: number }).status === 404) return null;
      throw error;
    }
  },

  /**
   * Move the period.
   *
   * No client-side transition check. The server holds the table, a period's
   * state can change between the screen loading and the button being pressed,
   * and a refusal naming the current state tells the reader more than a
   * disabled button that explains nothing.
   */
  async advance(id: string, to: PayrollRunStatus, _actor: unknown, reason?: string) {
    const action = TRANSITION_ACTIONS[to];
    if (!action) {
      throw new Error(`No payroll action reaches “${to}”.`);
    }

    await apiTransport().post(
      `/api/v1/payroll-periods/${id}/${action}`,
      reason ? { reason } : {},
      // Keyed: locking or approving twice because somebody double-clicked is
      // not something the audit trail should have to explain later.
      { idempotencyKey: newIdempotencyKey() },
    );

    const refreshed = await apiPayrollService.getRun(id);
    if (!refreshed) throw new Error('That payroll period disappeared during the transition.');
    return refreshed;
  },

  /**
   * Employee results.
   *
   * Behind `payroll.view_sensitive`, which is a different grant from
   * `payroll.view`: knowing a pay run exists and seeing what people earn are
   * separate permissions, and HR holds the first without the second. A caller
   * without it gets a refusal here rather than a page of blanked-out figures.
   */
  async lines(
    periodId: string,
    filter: { exceptionsOnly?: boolean } = {},
  ): Promise<Page<PayrollLine>> {
    const [rows, exceptions] = await Promise.all([
      apiTransport().get<ApiPayrollEmployee[]>(`/api/v1/payroll-periods/${periodId}/employees`),
      apiTransport()
        .get<ApiPayrollException[]>(`/api/v1/payroll-periods/${periodId}/exceptions`)
        // Exceptions need only payroll.view, so this rarely fails where the
        // list succeeded — but a missing exception list must not blank the
        // whole table.
        .catch(() => [] as ApiPayrollException[]),
    ]);

    const byEmployee = new Map<string, ApiPayrollException[]>();
    for (const exception of exceptions) {
      if (!exception.employeeId) continue;
      const existing = byEmployee.get(exception.employeeId) ?? [];
      existing.push(exception);
      byEmployee.set(exception.employeeId, existing);
    }

    const mapped = rows
      .map((row) => toPayrollLine(row, periodId, byEmployee.get(row.employeeId) ?? []))
      .filter((line) => !filter.exceptionsOnly || line.exceptions.length > 0);

    return { rows: mapped, total: mapped.length, page: 1, pageSize: mapped.length };
  },

  /**
   * The period's exceptions, blocking first, with who resolved each and
   * whether a reason can clear it at all — that is the server's call, made
   * per code, and the panel only offers what it will accept.
   */
  async exceptions(periodId: string): Promise<PayrollPeriodException[]> {
    const rows = await apiTransport().get<ApiPayrollException[]>(
      `/api/v1/payroll-periods/${periodId}/exceptions`,
    );
    return rows.map((row) => ({
      id: row.id,
      employeeId: row.employeeId,
      employeeName: row.employee?.fullName ?? null,
      employeeNumber: row.employee?.employeeNumber ?? null,
      code: row.code,
      severity: (['info', 'warning', 'blocking'].includes(row.severity)
        ? row.severity
        : 'warning') as PayrollPeriodException['severity'],
      message: row.message,
      suggestedAction: row.suggestedAction ?? null,
      resolvedAt: row.resolvedAt ?? null,
      resolvedByName: row.resolvedByName ?? null,
      resolutionNote: row.resolutionNote ?? null,
      waivable: row.waivable ?? false,
    }));
  },

  async exclusions(periodId: string): Promise<PayrollPeriodExclusion[]> {
    const rows = await apiTransport().get<
      {
        id: string;
        employeeId: string;
        reason: string;
        excludedByName: string | null;
        createdAt: string;
        employee: { fullName: string; employeeNumber: string } | null;
      }[]
    >(`/api/v1/payroll-periods/${periodId}/exclusions`);
    return rows.map((row) => ({
      id: row.id,
      employeeId: row.employeeId,
      employeeName: row.employee?.fullName ?? 'Former employee',
      employeeNumber: row.employee?.employeeNumber ?? '',
      reason: row.reason,
      excludedByName: row.excludedByName,
      createdAt: row.createdAt,
    }));
  },

  /** Resolve with a reason. Refused by the server for an exception a note cannot fix. */
  resolveException(periodId: string, exceptionId: string, note: string) {
    return apiTransport().post(
      `/api/v1/payroll-periods/${periodId}/exceptions/${exceptionId}/resolve`,
      { note },
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  /** Leave one employee out of this period's run, with a reason. */
  excludeEmployee(periodId: string, employeeId: string, reason: string) {
    return apiTransport().post(
      `/api/v1/payroll-periods/${periodId}/exclusions`,
      { employeeId, reason },
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  /** Take an exclusion back; a calculated period returns to input review. */
  includeEmployee(periodId: string, employeeId: string) {
    return apiTransport().delete(`/api/v1/payroll-periods/${periodId}/exclusions/${employeeId}`);
  },

  /** Publish payslips. Only from a locked period; the server enforces that. */
  publishPayslips(periodId: string) {
    return apiTransport().post(
      `/api/v1/payroll-periods/${periodId}/publish-payslips`,
      {},
      { idempotencyKey: newIdempotencyKey() },
    );
  },

  /**
   * Open a payroll period for one payroll group.
   *
   * Keyed: a retried request after a timeout must not open the same month
   * twice. Overlaps with an existing period are the server's to refuse.
   */
  async create(input: NewPayrollPeriod): Promise<PayrollRun> {
    return toPayrollRun(
      await apiTransport().post<ApiPayrollPeriod>('/api/v1/payroll-periods', input, {
        idempotencyKey: newIdempotencyKey(),
      }),
    );
  },
};
